import { useEffect } from "react";
import InputField from "../../../components/InputField";
import SelectField from "../../../components/SelectField";

export default function DetaliiStep({ data, errors, update }) {
  const indiciRON = [
    { value: "IRCC", label: "IRCC (BNR)" },
  ];
  const indiciEUR = [
    { value: "EURIBOR_3M", label: "EURIBOR 3M" },
    { value: "EURIBOR_6M", label: "EURIBOR 6M" },
  ];
  const indiciUSD = [
    { value: "SOFR_3M", label: "SOFR 3M (fost LIBOR)" },
  ];

  const indiciDisponibili = data.valuta === "EUR" ? indiciEUR : data.valuta === "USD" ? indiciUSD : indiciRON;
  const indiceValid = indiciDisponibili.some(i => i.value === data.dobandaIndice);

  // Limite suma: 50.000 EUR sau echivalent
  const limiteSuma = { EUR: 50000, USD: 55000, RON: 250000 };
  const limitaCurenta = limiteSuma[data.valuta] || 250000;
  const suma = Number(data.sumaImprumut) || 0;

  // Auto-reset indice when currency changes
  useEffect(() => {
    if (data.tipDobanda === "variabila" && !indiceValid) {
      update("dobandaIndice")(indiciDisponibili[0].value);
    }
  }, [data.valuta, data.tipDobanda, indiceValid, indiciDisponibili, update]);

  // Auto-adjust suma when currency changes and suma exceeds new limit
  useEffect(() => {
    if (suma > limitaCurenta) {
      update("sumaImprumut")(String(limitaCurenta));
    }
  }, [data.valuta, suma, limitaCurenta, update]);
  const depasesteLimit = suma > limitaCurenta;

  // Handler care limiteaza suma la max
  const handleSumaChange = (e) => {
    const val = e.target ? e.target.value : e;
    const numVal = Number(val);
    if (numVal > limitaCurenta) {
      update("sumaImprumut")(String(limitaCurenta));
    } else {
      update("sumaImprumut")(val);
    }
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
      <InputField label="Suma imprumutata" value={data.sumaImprumut} onChange={handleSumaChange} placeholder="ex: 10000" type="number" required half error={errors.sumaImprumut} max={limitaCurenta} />
      <SelectField
        label="Valuta"
        value={data.valuta}
        onChange={update("valuta")}
        options={[
          { value: "RON", label: "RON (Lei)" },
          { value: "EUR", label: "EUR (Euro)" },
          { value: "USD", label: "USD (Dolari)" },
        ]}
        half
      />
      <p style={{ width: "100%", fontSize: "11px", color: "#8a7e70", margin: "-8px 0 0 0" }}>
        Platforma este destinata imprumuturilor civile intre persoane fizice (max. {limitaCurenta.toLocaleString("ro-RO")} {data.valuta}).
      </p>
      {depasesteLimit && (
        <p style={{ width: "100%", fontSize: "11px", color: "#c45d3e", margin: "-4px 0 0 0", fontStyle: "italic" }}>
          ⚠️ Pentru sume mai mari, se recomanda contract autentic notarial.
        </p>
      )}
      <SelectField
        label="Scopul imprumutului"
        value={data.scopImprumut}
        onChange={update("scopImprumut")}
        options={[
          { value: "personal", label: "Necesitati personale" },
          { value: "medical", label: "Cheltuieli medicale" },
          { value: "studii", label: "Studii / Educatie" },
          { value: "afacere", label: "Investitie / Afacere" },
          { value: "altele", label: "Altele" },
        ]}
      />

      <SelectField
        label="Dobanda"
        value={data.tipDobanda}
        onChange={update("tipDobanda")}
        options={[
          { value: "fara", label: "Fara dobanda (gratuit)" },
          { value: "fixa", label: "Dobanda fixa (% pe an)" },
          { value: "variabila", label: "Dobanda variabila (avansat)" },
        ]}
      />

      {data.tipDobanda === "fixa" && (
        <>
          <InputField label="Dobanda anuala (%)" value={data.dobandaProcent} onChange={update("dobandaProcent")} placeholder="ex: 5" type="number" required half error={errors.dobandaProcent} min={0} max={20} step={0.5} />
          <p style={{ width: "100%", fontSize: "11px", color: "#8a7e70", margin: "-8px 0 0 0" }}>
            Dobanda se aplica anual si nu poate depasi 20% pe an.
          </p>
        </>
      )}

      {data.tipDobanda === "variabila" && (() => {
        // Valori medii istorice orientative pentru indici
        const indiceEstimat = data.valuta === "EUR" ? 3.5 : data.valuta === "USD" ? 5 : 6; // EURIBOR ~3.5%, SOFR ~5%, IRCC ~6%
        const marja = Number(data.dobandaMarja) || 0;
        const plafon = Number(data.dobandaPlafon) || 15;
        const dobandaEstimata = indiceEstimat + marja;
        const aproapeDeClafon = dobandaEstimata >= plafon * 0.8; // 80% din plafon

        return (
          <div style={{ width: "100%", padding: "16px", backgroundColor: "#fdfbf7", borderRadius: "8px", border: "1px solid #e8e0d4" }}>
            <p style={{ fontSize: "12px", color: "#8a7e70", margin: "0 0 12px 0", fontStyle: "italic" }}>
              ⚠️ Dobanda variabila trebuie sa aiba formula clara si plafon maxim pentru a fi valabila juridic.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
              <SelectField
                label={`Indice de referinta (${data.valuta})`}
                value={data.dobandaIndice}
                onChange={update("dobandaIndice")}
                options={indiciDisponibili}
                half
              />
              <InputField label="Marja fixa (% pe an)" value={data.dobandaMarja} onChange={update("dobandaMarja")} placeholder="ex: 2" type="number" required half error={errors.dobandaMarja} min={0} max={10} step={0.25} />
              <SelectField
                label="Plafon maxim dobanda (%)"
                value={data.dobandaPlafon}
                onChange={update("dobandaPlafon")}
                options={[
                  { value: "10", label: "10% / an" },
                  { value: "12", label: "12% / an" },
                  { value: "15", label: "15% / an" },
                  { value: "18", label: "18% / an" },
                  { value: "20", label: "20% / an (plafon maxim permis de platforma)" },
                ]}
                half
              />
            </div>
            <p style={{ fontSize: "11px", color: "#8a7e70", margin: "8px 0 0 0" }}>
              Dobanda estimativa curenta: ~{dobandaEstimata.toFixed(1)}% / an (indice ~{indiceEstimat}% + marja {marja}%). Plafonul maxim este limitat la 20% pe an.
            </p>
            {aproapeDeClafon && (
              <p style={{ fontSize: "11px", color: "#c45d3e", margin: "4px 0 0 0", fontStyle: "italic" }}>
                ⚠️ Dobanda poate atinge plafonul maxim in functie de evolutia indicelui de referinta.
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}
